import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getProblemDetail } from 'api/v1Helpers';
import { parishSelectionMoved } from 'redux/selectors/parishContext';
import { listParishPaymentRequests } from 'services/paymentRequestsApi';
import type { PaymentRequest } from 'types/paymentRequests';

interface SentInvoicesState {
    items: PaymentRequest[];
    total: number;
    loading: boolean;
    error: string | null;
    inflightParishId: string | null;
    /** RTK requestId — drop stale page responses (same pattern as reports). */
    latestRequestId: string | null;
}

const initialState: SentInvoicesState = {
    items: [],
    total: 0,
    loading: false,
    error: null,
    inflightParishId: null,
    latestRequestId: null,
};

export type FetchSentInvoicesArg = {
    parishId: string;
    page?: number;
    pageSize?: number;
};

/**
 * Parish-wide invoice list: every payment request sent by any staff member of `parishId`,
 * not just the caller's own. Backend: GET /v1/parishes/:parishId/payment-requests (CLERK+).
 * `GET /v1/payment-requests?role=sender|recipient` is a separate, user-scoped contract the
 * Mobile App depends on — this thunk does not call it or route through it.
 */
export const fetchSentInvoices = createAsyncThunk<
    Awaited<ReturnType<typeof listParishPaymentRequests>> | null,
    FetchSentInvoicesArg,
    { rejectValue: string }
>('walletInvoices/fetchSentInvoices', async ({ parishId, page = 1, pageSize = 20 }, { getState, rejectWithValue }) => {
    if (!parishId) return null;
    try {
        const result = await listParishPaymentRequests(parishId, { page, pageSize });
        if (parishSelectionMoved(getState, parishId)) return null;
        return result;
    } catch (err) {
        if (parishSelectionMoved(getState, parishId)) return null;
        return rejectWithValue(getProblemDetail(err) || 'Failed to load invoices');
    }
});

const walletInvoicesSlice = createSlice({
    name: 'walletInvoicesList',
    initialState,
    reducers: {
        clearSentInvoices() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchSentInvoices.pending, (state, action) => {
            const nextParishId = action.meta.arg.parishId;
            state.loading = true;
            state.error = null;
            // Soft refresh (same parish, e.g. next page): keep prior rows to avoid flicker.
            // Parish switch: drop rows so another parish's invoices cannot paint.
            if (state.inflightParishId !== nextParishId) {
                state.items = [];
                state.total = 0;
            }
            state.inflightParishId = nextParishId;
            state.latestRequestId = action.meta.requestId;
        });
        builder.addCase(fetchSentInvoices.fulfilled, (state, action) => {
            if (action.meta.requestId !== state.latestRequestId) return;
            state.loading = false;
            if (!action.payload) return;
            state.items = action.payload.items;
            state.total = action.payload.total;
            state.error = null;
        });
        builder.addCase(fetchSentInvoices.rejected, (state, action) => {
            if (action.meta.requestId !== state.latestRequestId) return;
            state.loading = false;
            state.error = action.payload || action.error.message || 'Failed to load invoices';
        });
    },
});

export const { clearSentInvoices } = walletInvoicesSlice.actions;
export default walletInvoicesSlice.reducer;
